import { Question, Tier } from './types';

export const PILLARS = [
  'Strategic & Product Readiness',
  'Architecture Elasticity',
  'Data Activation',
  'Engineering Velocity',
  'Product Adaptability',
];

export const QUESTIONS: Question[] = [
  {
    pillar: 0,
    q: "How clearly is AI defined as a core capability in your product's roadmap for the next 12 months?",
    opts: [
      'AI is explicitly scoped with defined use cases tied to product outcomes',
      'AI is on the roadmap but use cases are still exploratory or undefined',
      "AI has been discussed but hasn't made it onto the formal roadmap yet",
      'AI is not currently part of our product roadmap',
    ],
    scores: [4, 2, 1, 0],
  },
  {
    pillar: 0,
    q: "How well does your product's current feature set reflect intentional AI design — versus AI capabilities added reactively or experimentally?",
    opts: [
      "AI capabilities are architected into the product's core UX and data flows",
      "We've shipped AI features, but they sit alongside the product rather than inside it",
      "We've experimented with AI features but nothing is in production",
      'Our product has no AI features today',
    ],
    scores: [4, 2, 1, 0],
  },
  {
    pillar: 1,
    q: "How well does your product's architecture support the integration and iteration of AI/ML components?",
    opts: [
      'Modular and API-first — we can add, swap, or version AI components independently',
      'We could integrate AI components but it would require meaningful refactoring',
      'Our architecture is tightly coupled in ways that would make AI integration slow and risky',
      "We haven't evaluated our architecture against AI integration requirements",
    ],
    scores: [4, 3, 1, 0],
  },
  {
    pillar: 2,
    q: "How readily can your product's data be used to train, fine-tune, or evaluate AI models?",
    opts: [
      'Our product generates clean, labeled, structured data already used for model training or evaluation',
      "We have usable data but it requires significant transformation before it's model-ready",
      'Data exists but is siloed, inconsistently structured, or lacks the labeling needed for AI use',
      "We don't yet have a data layer capable of supporting AI model development",
    ],
    scores: [4, 2, 1, 0],
  },
  {
    pillar: 2,
    q: 'How close to real-time does behavioral or usage data from your product flow into AI-driven features or decision logic?',
    opts: [
      'Product behavior feeds AI logic in near real-time (sub-minute or event-driven)',
      'Data flows on a batch schedule (hourly or daily) and AI features reflect that lag',
      'Data is collected but not yet connected to any AI decision layer',
      "We don't currently capture structured behavioral data from the product",
    ],
    scores: [4, 3, 1, 0],
  },
  {
    pillar: 3,
    q: "How would you characterize your product's current release infrastructure?",
    opts: [
      'Fully automated CI/CD with feature flagging, rollback capability, and environment parity',
      'CI/CD is in place but release processes still involve manual steps or gates',
      'Releases are largely manual or infrequent, with limited automation',
      'Our release process is ad hoc with no formal pipeline',
    ],
    scores: [4, 3, 1, 0],
  },
  {
    pillar: 4,
    q: 'How is your product instrumented to measure the performance of AI-driven features once shipped?',
    opts: [
      'We have observability tooling specifically for AI outputs — monitoring drift, quality, and downstream impact',
      'We track AI feature performance through general product analytics, but no AI-specific instrumentation',
      "We ship AI features but have limited visibility into how they're performing in production",
      'We have no current framework for evaluating AI feature performance post-launch',
    ],
    scores: [4, 3, 1, 0],
  },
  {
    pillar: 4,
    q: "How modifiable is your product's AI behavior without requiring a full engineering cycle?",
    opts: [
      'We can adjust model parameters, prompts, weights, or decision thresholds via config or tooling — no redeploy needed',
      'Some AI behaviors can be tuned without a full release, but most changes require engineering involvement',
      'Any change to AI behavior requires a full development and release cycle',
      "We don't have AI behavior in production to modify yet",
    ],
    scores: [4, 3, 1, 0],
  },
];

export const TIERS: Tier[] = [
  { label: 'Not Yet Ready', range: '0–24', min: 0, max: 24 },
  { label: 'Early Stage', range: '25–49', min: 25, max: 49 },
  { label: 'Developing', range: '50–74', min: 50, max: 74 },
  { label: 'Advanced', range: '75–89', min: 75, max: 89 },
  { label: 'AI-Native Ready', range: '90–100', min: 90, max: 100 },
];

export const TIME_SLOTS = ['Mon 9am', 'Mon 11am', 'Tue 10am', 'Tue 2pm', 'Wed 9am', 'Wed 3pm'];

export const PILLAR_LABELS = [
  { key: 'strategy' as const, label: 'Strategic & Product Readiness' },
  { key: 'arch' as const, label: 'Architecture Elasticity' },
  { key: 'data' as const, label: 'Data Activation' },
  { key: 'engVel' as const, label: 'Engineering Velocity' },
  { key: 'org' as const, label: 'Product Adaptability' },
];
